import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getLogger, trackHeapEvent } from "@hoteltradergit/ht-telemetry-ui-lib";
import { getReservationDailyRates } from "../../../api";
import { ANALYTICS_EVENTS } from "../../../constants/analyticsEvents";
import { DAILY_RATES_STATUS, DAILY_RATES_TIMEOUT_MS } from "../constants";

// Expansion state + per-confirmation-code cache for the reservation daily
// breakdown. Rates are fetched lazily on first expand; collapsing and
// re-expanding within the SAME result set reuses the cache. Both the
// expansion and the cache reset on every new result set (searchSequence):
// a confirmation code is immutable but the rates behind it are NOT — a
// reservation cancelled or modified in another portal comes back with
// different rate details, and serving the cached pre-change breakdown next
// to its fresh status would show wrong financial data (EO-1927 review).
const useDailyRates = ({ searchSequence }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [dailyRatesByCode, setDailyRatesByCode] = useState({});
  // Bumped whenever the cache is cleared. A fetch still in flight across a
  // re-search belongs to the PREVIOUS result set; without this guard its late
  // response would write pre-re-search rates back into the freshly cleared
  // cache, and the next expand would trust that LOADED entry — resurrecting
  // the exact stale-rates bug the clear exists to prevent.
  const cacheGenerationRef = useRef(0);

  const fetchDailyRates = useCallback(async (htConfirmationCode) => {
    const generation = cacheGenerationRef.current;
    setDailyRatesByCode((prev) => ({
      ...prev,
      [htConfirmationCode]: { status: DAILY_RATES_STATUS.LOADING, rates: [] },
    }));
    // The shared axios instance sets no timeout, and a request that never
    // settles would strand the entry in LOADING — a state the re-expand
    // retry below deliberately skips. Racing a local timeout converts a hung
    // request into the ERROR path, where re-expanding retries it.
    let timeoutId;
    try {
      const response = await Promise.race([
        getReservationDailyRates([htConfirmationCode]),
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("Daily rates request timed out")),
            DAILY_RATES_TIMEOUT_MS
          );
        }),
      ]);
      // A late response from a previous result set must be discarded, not
      // written into the fresh cache — see cacheGenerationRef.
      if (generation !== cacheGenerationRef.current) return;
      const rates = response.data?.[htConfirmationCode] || [];
      setDailyRatesByCode((prev) => ({
        ...prev,
        [htConfirmationCode]: { status: DAILY_RATES_STATUS.LOADED, rates },
      }));
    } catch (error) {
      getLogger().error("ReservationReport", {
        type: "Error",
        page_url: window.location.href,
        error_desc: `Failed to fetch daily rates for ${htConfirmationCode}`,
        error_stack: error?.stack ?? null,
      });
      // Stale-generation failures still log above, but must neither toast at
      // a user who has moved on nor write ERROR into the new cache.
      if (generation !== cacheGenerationRef.current) return;
      setDailyRatesByCode((prev) => ({
        ...prev,
        [htConfirmationCode]: { status: DAILY_RATES_STATUS.ERROR, rates: [] },
      }));
      toast.error("Failed to load the daily breakdown");
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const handleToggleExpand = useCallback(
    (htConfirmationCode) => {
      const isExpanding = !expandedRowKeys.includes(htConfirmationCode);
      // EO-1562: custom events (not anchor-id autocapture) so they arrive in
      // Heap ready to report on. Fired here — the user-click path — and NOT
      // from the searchSequence reset, which collapses rows programmatically.
      trackHeapEvent(
        isExpanding
          ? ANALYTICS_EVENTS.DAILY_BREAKDOWN_EXPANDED
          : ANALYTICS_EVENTS.DAILY_BREAKDOWN_COLLAPSED,
        { ht_confirmation_code: htConfirmationCode }
      );
      setExpandedRowKeys((prev) =>
        isExpanding
          ? [...prev, htConfirmationCode]
          : prev.filter((key) => key !== htConfirmationCode)
      );
      const entry = dailyRatesByCode[htConfirmationCode];
      // No entry yet → first expand; error entry → re-expanding doubles as retry.
      if (
        isExpanding &&
        (!entry || entry.status === DAILY_RATES_STATUS.ERROR)
      ) {
        // Fire-and-forget by design: progress is reported through the
        // per-code status entry, and fetchDailyRates traps every rejection
        // internally (ERROR state + log + toast), so there is nothing to
        // await or catch here. `void` marks that intent for Sonar (S6544).
        void fetchDailyRates(htConfirmationCode);
      }
    },
    [expandedRowKeys, dailyRatesByCode, fetchDailyRates]
  );

  // Collapse everything AND drop the cache whenever a new result set is
  // fetched — search, re-search, property switch, sort, page change. The
  // rows are different records each time: carried-over keys would render
  // pre-expanded (EO-1895), and carried-over rates would show pre-change
  // amounts for a reservation modified elsewhere (see header comment).
  // useLayoutEffect, not useEffect: the sequence bump commits in the same
  // render as the new rows, and a plain effect runs after paint — one frame
  // of a new row drawn expanded with the old breakdown when its code exists
  // in both result sets. Resetting before paint removes that flash.
  useLayoutEffect(() => {
    cacheGenerationRef.current += 1;
    setExpandedRowKeys([]);
    setDailyRatesByCode({});
  }, [searchSequence]);

  return { expandedRowKeys, dailyRatesByCode, handleToggleExpand };
};

export default useDailyRates;
